import type { TransferItem } from '@kontor/shared'
import { create } from 'zustand'

interface TransferStore {
  /** In the order the backend queued them, which is the order the user picked. */
  items: TransferItem[]
  /** When the live channel last delivered a state for an item, keyed by id. */
  updatedAt: Record<string, number>
  apply: (item: TransferItem) => void
  /**
   * Takes the REST snapshot of the queue. `requestedAt` is when that request went out: anything
   * the live channel delivered after that is newer than the snapshot and must survive it,
   * otherwise a slow response silently rewinds a transfer to an earlier state.
   */
  hydrate: (snapshot: TransferItem[], requestedAt: number) => void
  clear: () => void
}

export const useTransferStore = create<TransferStore>((set) => ({
  items: [],
  updatedAt: {},
  apply: (item) =>
    set((store) => {
      const updatedAt = { ...store.updatedAt, [item.id]: Date.now() }
      const index = store.items.findIndex((entry) => entry.id === item.id)
      if (index === -1) return { items: [...store.items, item], updatedAt }
      const items = [...store.items]
      items[index] = item
      return { items, updatedAt }
    }),
  hydrate: (snapshot, requestedAt) =>
    set((store) => {
      const known = new Map(store.items.map((item) => [item.id, item]))
      return {
        items: snapshot.map((item) => {
          const live = known.get(item.id)
          const liveAt = store.updatedAt[item.id] ?? 0
          return live !== undefined && liveAt > requestedAt ? live : item
        }),
      }
    }),
  clear: () => set({ items: [], updatedAt: {} }),
}))

/** Everything still moving or waiting for an answer, in one number for the badge. */
export function activeCount(items: TransferItem[]): number {
  return items.filter(
    (item) => item.status === 'queued' || item.status === 'running' || item.status === 'conflict',
  ).length
}

export function firstConflict(items: TransferItem[]): TransferItem | undefined {
  return items.find((item) => item.status === 'conflict')
}
